import React, { useState, useEffect, useCallback } from 'react';
import { connect } from 'react-redux';
import moment from 'moment';

import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';

import { Calendar, DayInfo, DayInfoMobile } from '../../components/calendar';
import LoadScreen from '../../components/load-screen';
import CaseModal from '../../components/modals/case';
import CaseExportModal from '../../components/modals/case/CaseExportModal';

import {
  discardActiveCase,
  setActiveCase,
  setActiveTab,
  fetchCases
} from '../../actions/casesActions';

import { procedureFlows, cmfExtendedProcedureFlows } from '../../constants/cases';

import './calendar-page.scss';

const CalendarPage = (props) => {
  const {
    casesLoading,
    casesLoaded,
    setActiveCase,
    discardActiveCase,
    goToFiles,
    caseId,
    fetchCases,
    trainingMode,
  } = props;

  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [month, setMonth] = useState(new Date());
  const [selectedDay, setSelectedDay] = useState(new Date());
  const [dayCases, setDayCases] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [isExportModalOpen, setIsExportModalOpen] = useState(false);
  const [cases, setCases] = useState([]);
  const [, setLoading] = useState(false);

  const getCases = useCallback(async () => {
    try {
      setLoading(true);

      const dateFrom = moment(month).startOf('month').valueOf() - 1;
      const dateTo = moment(month).endOf('month').valueOf() + 1;

      const params = {
        perPage: 250,
        dateFrom,
        dateTo,
        trainingMode,
      };

      const res = await fetchCases(params);

      if (res?.cases) {
        setCases(
          res.cases.map((c) => ({
            ...c,
            access: true,
            date: c?.date ? new Date(c.date) : '',
          }))
        );
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [fetchCases, month, trainingMode]);

  useEffect(() => {
    getCases();
  }, [getCases]);

  const handleAddClick = () => {
    setIsOpen(true);
  };

  const handleModalClose = (isFirstStep, selectedFlow) => {
    setIsOpen(false);

    if (typeof isFirstStep === 'boolean' && isFirstStep) {
      if (
        [
          procedureFlows.CMF,
          ...cmfExtendedProcedureFlows,
        ].includes(selectedFlow)
      ) {
        setIsOpen(true);
      } else {
        setIsExportModalOpen(false);
      }
    } else {
      discardActiveCase();
    }
  };

  const goToCase = (id) => {
    setIsOpen(true);
    setActiveCase({ id });
  };

  const goToFilesHandler = (id) => {
    goToCase(id);
    goToFiles();
  };

  const loading = !casesLoaded && casesLoading;

  return (
    <div className="calendar-page">
      {loading && <LoadScreen />}

      <Calendar
        selectedDay={selectedDay}
        onDaySelect={(day, casesForDay) => {
          setSelectedDay(day);
          setDayCases(casesForDay);
        }}
        cases={cases}
        handleAddClick={handleAddClick}
        month={month}
        setMonth={setMonth}
      />

      {isDesktop && (
        <DayInfo
          selectedDay={selectedDay}
          cases={dayCases}
          goToCase={goToCase}
          goToFiles={goToFilesHandler}
        />
      )}

      {isMobile && (
        <div className="info-mobile-container">
          <DayInfoMobile
            selectedDay={selectedDay}
            cases={dayCases}
            goToCase={goToCase}
          />
        </div>
      )}

      <CaseModal open={isOpen} onClose={handleModalClose} />

      <CaseExportModal
        open={isExportModalOpen}
        caseId={caseId}
        onClose={() => {
          setIsExportModalOpen(false);
          discardActiveCase();
        }}
        onSkipClick={() => {
          setIsExportModalOpen(false);
          discardActiveCase();
        }}
      />
    </div>
  );
};

const mapStateToProps = (state) => ({
  caseId: state.cases?.activeCase?.id,
  trainingMode: state.sidebar.trainingMode,
});

const mapDispatchToProps = (dispatch) => ({
  setActiveCase: (caseItem) => dispatch(setActiveCase(caseItem)),
  discardActiveCase: () => dispatch(discardActiveCase()),
  goToFiles: () => dispatch(setActiveTab(2)),
  fetchCases: (params) => dispatch(fetchCases(params)),
});

export default connect(mapStateToProps, mapDispatchToProps)(CalendarPage);
